import React, { useCallback, useRef, useState } from 'react';
import { Popup } from 'semantic-ui-react';
import { FILAS_VTME_THEME } from './filasVtmePageTheme';

export type FilasVtmeHelpPosition =
  | 'top left'
  | 'top right'
  | 'bottom left'
  | 'bottom right'
  | 'bottom center'
  | 'top center'
  | 'right center'
  | 'left center';

const POPUP_MODIFIERS = [
  {
    name: 'preventOverflow',
    options: {
      boundary: 'viewport',
      padding: 12,
      altAxis: true,
      tether: false,
    },
  },
  {
    name: 'flip',
    options: {
      padding: 12,
      fallbackPlacements: [
        'bottom left',
        'bottom right',
        'bottom center',
        'top left',
        'top right',
        'top center',
        'right center',
        'left center',
      ],
    },
  },
  {
    name: 'offset',
    options: {
      offset: [0, 8],
    },
  },
];

function availableWidthNear(el: HTMLElement | null): number {
  if (typeof window === 'undefined') return 280;
  const viewportCap = Math.max(160, window.innerWidth - 24);
  if (!el) return Math.min(280, viewportCap);

  const rect = el.getBoundingClientRect();
  const pad = 24;
  const toRight = window.innerWidth - rect.left - pad;
  const toLeft = rect.right - pad;
  const best = Math.max(toRight, toLeft, 160);
  return Math.min(320, viewportCap, Math.max(160, Math.floor(best)));
}

type Props = {
  text: string;
  position?: FilasVtmeHelpPosition;
  /** Ícone discreto no canto (ex.: cards de KPI). */
  compact?: boolean;
  /** Estilo absoluto no canto do card. */
  corner?: boolean;
};

/**
 * Ajuda flutuante da tela Filas VTME: fica na viewport (flip/overflow)
 * e quebra linha quando o texto não cabe na largura disponível.
 */
export const FilasVtmeHelpPopup: React.FC<Props> = ({
  text,
  position = 'bottom left',
  compact = false,
  corner = false,
}) => {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [maxWidth, setMaxWidth] = useState(280);

  const handleOpen = useCallback(() => {
    setMaxWidth(availableWidthNear(triggerRef.current));
  }, []);

  return (
    <Popup
      on="hover"
      hoverable
      flowing
      position={position}
      positionFixed
      popperModifiers={POPUP_MODIFIERS}
      onOpen={handleOpen}
      className="cqv-sla-help-popup"
      style={{
        maxWidth,
        width: 'max-content',
        padding: '10px 12px',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      }}
      content={
        <div
          style={{
            maxWidth: '100%',
            width: '100%',
            boxSizing: 'border-box',
            lineHeight: 1.45,
            fontSize: 13,
            color: FILAS_VTME_THEME.text,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            hyphens: 'auto',
          }}
        >
          {text}
        </div>
      }
      trigger={
        <span
          ref={triggerRef}
          role="img"
          aria-label="Ajuda"
          style={{
            marginLeft: corner ? 0 : 4,
            cursor: 'help',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: compact ? 11 : 12,
            fontWeight: 700,
            lineHeight: 1,
            color: FILAS_VTME_THEME.muted,
            opacity: corner ? 0.7 : 1,
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            background: 'transparent',
            ...(corner
              ? {
                  position: 'absolute',
                  top: 4,
                  right: 6,
                  zIndex: 1,
                }
              : null),
          }}
        >
          ?
        </span>
      }
    />
  );
};

export default FilasVtmeHelpPopup;
